import { useQuery } from '@tanstack/react-query'
import type { TimePeriod } from '@/types/dashboard'
import { supabase } from '@/lib/supabase'
import { captureError } from '@/lib/sentry'
import {
  DEXTORO_EFFECTIVE_RATE_GRADUATED,
  CREATOR_RATE_GRADUATED_DEFAULT,
} from '@/lib/constants/feeShares'

// Single source of truth for dextoro revenue. The edge function owns the
// canonical math and accepts a `period` parameter so 24h/7d/30d/all return
// distinct figures.
const EDGE_URL = 'https://vkhijzofpanjuolcdazv.supabase.co/functions/v1/protocol-revenue'

const METEORA_BC_FEE_SHARE = 0.20
const PROTOCOL_SHARE_BC = 0.70
const DEXTORO_BC_EFFECTIVE = (1 - METEORA_BC_FEE_SHARE) * PROTOCOL_SHARE_BC // 0.56
const CREATOR_BC_EFFECTIVE = (1 - METEORA_BC_FEE_SHARE) * (1 - PROTOCOL_SHARE_BC) // 0.24

const DAMM_V2_DEXTORO_EFFECTIVE_RATE = DEXTORO_EFFECTIVE_RATE_GRADUATED
// 0.40% Meteora protocol cut on DAMM V2 (20% of the 2% total fee).
const DAMM_V2_METEORA_RATE = METEORA_BC_FEE_SHARE * 0.02
const DAMM_V2_CREATOR_RATE = CREATOR_RATE_GRADUATED_DEFAULT

export interface ProtocolRevenue {
  // dextoro revenue (what we actually retain)
  totalSOL: number
  change24h: number
  tradingRevenueSOL: number
  migrationRevenueSOL: number
  dammV2RevenueSOL: number
  lpFeesSOL: number
  lpFeesClaimableSOL: number
  lpFeesClaimedSOL: number
  lpFeesPositionCount: number
  bcRevenueSOL: number
  graduatedRevenueSOL: number
  periodRevenueSOL: number
  previousPeriodRevenueSOL: number
  growthRatePercent: number
  avgRevenuePerToken: number
  // All-party breakdown
  totalProtocolFeesSOL: number
  meteoraRevenueSOL: number
  creatorRevenueSOL: number
  // Diagnostics
  dammV2ClaimableSkipped?: boolean
  lpFeesSkipped?: boolean
  lastComputedAt?: string
}

interface EdgeProtocolRevenueResponse {
  total_sol: number
  trading_sol: number
  migration_sol: number
  damm_v2_sol: number
  damm_v2_claimable_skipped?: boolean
  lp_fees_sol?: number
  lp_fees_claimable_sol?: number
  lp_fees_claimed_sol?: number
  lp_fees_position_count?: number
  lp_fees_skipped?: boolean
  last_computed_at: string
  period: TimePeriod
}

async function fetchTotalTokenCount(): Promise<number> {
  const { count, error } = await supabase
    .from('launched_tokens')
    .select('*', { count: 'exact', head: true })
  if (error) return 0
  return count ?? 0
}

function deriveAllPartyBreakdown(edge: EdgeProtocolRevenueResponse) {
  // Split trading_sol into BC and DAMM V2 contributions using the effective
  // rates. We don't know the exact BC-vs-graduated mix from the edge response,
  // so back-calculate volumes and redistribute. `trading_sol` = dextoro's BC
  // share + dextoro's DAMM V2 share (which is also captured separately in
  // damm_v2_sol as claimed + on-chain claimable). Use damm_v2_sol as the
  // DAMM V2 portion; treat the rest as BC.
  const dammV2DextoroShare = edge.damm_v2_sol
  const bcDextoroShare = Math.max(edge.trading_sol - dammV2DextoroShare, 0)

  const bcTotalVolumeFee = DEXTORO_BC_EFFECTIVE > 0 ? bcDextoroShare / DEXTORO_BC_EFFECTIVE : 0
  const bcCreatorFee = bcTotalVolumeFee * CREATOR_BC_EFFECTIVE
  const bcMeteoraFee = bcTotalVolumeFee * METEORA_BC_FEE_SHARE

  const dammV2Volume = DAMM_V2_DEXTORO_EFFECTIVE_RATE > 0
    ? dammV2DextoroShare / DAMM_V2_DEXTORO_EFFECTIVE_RATE
    : 0
  const dammV2CreatorFee = dammV2Volume * DAMM_V2_CREATOR_RATE
  const dammV2MeteoraFee = dammV2Volume * DAMM_V2_METEORA_RATE

  // Migration fees + DTR LP fees: 100% to dextoro, no creator/meteora cut to
  // attribute (DTR LP is protocol-owned; the Meteora protocol-fee portion is
  // already excluded upstream by the LP-fee accumulator).
  const creatorRevenueSOL = bcCreatorFee + dammV2CreatorFee
  const meteoraRevenueSOL = bcMeteoraFee + dammV2MeteoraFee
  const totalProtocolFeesSOL = edge.total_sol + creatorRevenueSOL + meteoraRevenueSOL

  return {
    bcRevenueSOL: bcDextoroShare,
    graduatedRevenueSOL: dammV2DextoroShare,
    creatorRevenueSOL,
    meteoraRevenueSOL,
    totalProtocolFeesSOL,
  }
}

export function useProtocolRevenue(period: TimePeriod = '7d') {
  return useQuery<ProtocolRevenue>({
    queryKey: ['protocol-revenue-edge', period],
    queryFn: async () => {
      const res = await fetch(`${EDGE_URL}?period=${period}`)
      if (!res.ok) throw new Error(`protocol-revenue HTTP ${res.status}`)
      const edge = (await res.json()) as EdgeProtocolRevenueResponse

      const totalTokenCount = (await fetchTotalTokenCount()) || 1
      const { bcRevenueSOL, graduatedRevenueSOL, creatorRevenueSOL, meteoraRevenueSOL, totalProtocolFeesSOL } =
        deriveAllPartyBreakdown(edge)

      return {
        totalSOL: edge.total_sol,
        change24h: 0,
        tradingRevenueSOL: edge.trading_sol,
        migrationRevenueSOL: edge.migration_sol,
        dammV2RevenueSOL: edge.damm_v2_sol,
        lpFeesSOL: edge.lp_fees_sol ?? 0,
        lpFeesClaimableSOL: edge.lp_fees_claimable_sol ?? 0,
        lpFeesClaimedSOL: edge.lp_fees_claimed_sol ?? 0,
        lpFeesPositionCount: edge.lp_fees_position_count ?? 0,
        bcRevenueSOL,
        graduatedRevenueSOL,
        periodRevenueSOL: edge.total_sol,
        previousPeriodRevenueSOL: 0,
        growthRatePercent: 0,
        avgRevenuePerToken: edge.total_sol / totalTokenCount,
        totalProtocolFeesSOL,
        meteoraRevenueSOL,
        creatorRevenueSOL,
        dammV2ClaimableSkipped: edge.damm_v2_claimable_skipped,
        lpFeesSkipped: edge.lp_fees_skipped,
        lastComputedAt: edge.last_computed_at,
      }
    },
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    retry: (failureCount, error) => {
      if (failureCount >= 2) {
        captureError(
          error instanceof Error ? error : new Error('protocol-revenue fetch failed'),
          { feature: 'admin_dashboard', component: 'useProtocolRevenue' },
        )
      }
      return failureCount < 2
    },
  })
}
